/**
 * Carte diplomatique — un monde imaginaire en carte politique gravée.
 *
 * Un continent unique partitionné en régions ADJACENTES : l'Hexagone au
 * centre, les 7 pays voisins en couronne autour. Les frontières sont des
 * traits d'encre fins (légèrement tremblés par un filtre de bruit, pour le
 * rendu « tracé à la main »), comme sur une vraie carte papier. Le fond de
 * chaque pays est teinté selon la proximité politique. Méridiens et
 * parallèles ténus en arrière-plan. Les pays non découverts gardent leurs
 * frontières mais restent sans nom (terres encore inconnues).
 */

import type { WorldState } from "../engine/types";
import { HOME_COUNTRY } from "../engine/homeCountry";

interface Props {
  state: WorldState;
}

type Pt = [number, number];

// --- Géométrie du continent imaginaire (viewBox 0 0 340 200) ----------
const CENTER: Pt = [168, 100];

/** Heptagone intérieur = l'Hexagone (pays joueur). */
const INNER: Pt[] = [
  [168, 66],
  [206, 80],
  [212, 118],
  [180, 142],
  [146, 140],
  [128, 108],
  [134, 78],
];

/** Sommets de côte (frontière externe du continent). */
const OUTER: Pt[] = [
  [166, 30],
  [250, 52],
  [298, 118],
  [236, 172],
  [150, 178],
  [70, 150],
  [58, 58],
];

/** Point de côte intermédiaire entre OUTER[k] et OUTER[k+1] (irrégularité). */
const MID: Pt[] = [
  [212, 36],
  [286, 80],
  [280, 160],
  [192, 182],
  [104, 176],
  [48, 104],
  [106, 34],
];

/** Pays de la couronne, dans l'ordre des secteurs (k = 0..6). */
const RING_LABELS = [
  "Le Tsarat",
  "Le Dragon Rouge",
  "L'Émirat du Soleil",
  "Fédération Tropicale",
  "Confédération Alpine",
  "République de l'Aigle",
  "Union du Blé",
];

const path = (pts: Pt[]): string =>
  pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]} ${p[1]}`).join(" ") + " Z";

const avg = (pts: Pt[]): Pt => [
  pts.reduce((s, p) => s + p[0], 0) / pts.length,
  pts.reduce((s, p) => s + p[1], 0) / pts.length,
];

interface Region {
  label: string;
  d: string;
  lx: number;
  ly: number;
}

/** Régions de la couronne — chaque secteur va de l'arête intérieure à la côte. */
const RING: Region[] = RING_LABELS.map((label, k) => {
  const a = INNER[k];
  const b = INNER[(k + 1) % 7];
  const oa = OUTER[k];
  const ob = OUTER[(k + 1) % 7];
  const mid = MID[k];
  const d = path([a, b, ob, mid, oa]);
  // Ancre du label : biais vers la côte pour rester dans le corps du pays.
  const innerMid = avg([a, b]);
  const outerMid = avg([oa, ob]);
  const lx = innerMid[0] * 0.4 + outerMid[0] * 0.6;
  const ly = innerMid[1] * 0.4 + outerMid[1] * 0.6;
  return { label, d, lx, ly };
});

const HOME_REGION: Region = {
  label: HOME_COUNTRY.label,
  d: path(INNER),
  lx: CENTER[0],
  ly: CENTER[1],
};

/** Contour complet du continent (pour la côte épaissie). */
const COAST = path(OUTER.flatMap((o, k) => [o, MID[k]] as Pt[]));

/** Pôle de proximité → teinte de fond + libellé (bordures uniformes encre). */
function proximity(score: number): {
  fill: string;
  ink: string;
  label: string;
} {
  if (score >= 70)
    return { fill: "rgba(36,67,95,0.32)", ink: "#24435f", label: "Allié" };
  if (score >= 55)
    return { fill: "rgba(36,67,95,0.16)", ink: "#24435f", label: "Cordial" };
  if (score >= 40)
    return { fill: "rgba(169,116,58,0.22)", ink: "#a9743a", label: "Tendu" };
  if (score >= 25)
    return { fill: "rgba(155,28,28,0.20)", ink: "#9b1c1c", label: "Hostile" };
  return { fill: "rgba(155,28,28,0.42)", ink: "#9b1c1c", label: "Rupture" };
}

/** Découpe un nom long en deux lignes équilibrées. */
function wrapName(name: string): string[] {
  if (name.length <= 12) return [name];
  const words = name.split(" ");
  const half = Math.ceil(name.length / 2);
  let l1 = "";
  let l2 = "";
  for (const w of words) {
    if (!l2 && (l1 ? l1.length + 1 + w.length : w.length) <= half) {
      l1 = l1 ? `${l1} ${w}` : w;
    } else {
      l2 = l2 ? `${l2} ${w}` : w;
    }
  }
  return l2 ? [l1, l2] : [l1];
}

function Label({
  region,
  sub,
  subColor,
  home,
}: {
  region: Region;
  sub?: string;
  subColor?: string;
  home?: boolean;
}) {
  const lines = wrapName(region.label);
  return (
    <text
      x={region.lx}
      y={region.ly - (lines.length - 1) * 4}
      className={home ? "wm-name wm-name-home" : "wm-name"}
      textAnchor="middle"
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={region.lx} dy={i === 0 ? 0 : 8.5}>
          {ln}
        </tspan>
      ))}
      {sub && (
        <tspan
          x={region.lx}
          dy={9}
          className="wm-sub"
          style={subColor ? { fill: subColor } : undefined}
        >
          {sub}
        </tspan>
      )}
    </text>
  );
}

export function WorldMap({ state }: Props) {
  const byLabel = new Map(state.geopolitics.countries.map((c) => [c.label, c]));

  return (
    <figure className="world-map">
      <svg
        viewBox="0 0 340 200"
        role="img"
        aria-label="Carte diplomatique du monde"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Tremblé « tracé à la main » : bruit fractal léger déplaçant les
              traits. */}
          <filter id="wm-rough" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.03"
              numOctaves={2}
              seed={7}
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={2.4}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>

        {/* Mer */}
        <rect x="0" y="0" width="340" height="200" className="wm-sea" />

        {/* Méridiens + parallèles ténus */}
        <g className="wm-grat">
          {[40, 80, 120, 160, 200, 240, 280, 320].map((x) => (
            <line key={`v${x}`} x1={x} y1="0" x2={x} y2="200" />
          ))}
          {[40, 80, 120, 160].map((y) => (
            <line key={`h${y}`} x1="0" y1={y} x2="340" y2={y} />
          ))}
        </g>

        {/* Continent : régions teintées + frontières d'encre (tremblées) */}
        <g filter="url(#wm-rough)">
          {RING.map((region) => {
            const c = byLabel.get(region.label);
            const known = c?.known_to_player;
            const fill = known
              ? proximity(c!.political_relation).fill
              : "rgba(170,160,137,0.16)";
            return (
              <path
                key={region.label}
                d={region.d}
                className={known ? "wm-land" : "wm-land wm-unknown"}
                style={{ fill }}
              />
            );
          })}
          <path d={HOME_REGION.d} className="wm-land wm-home" />
          {/* Côte légèrement épaissie par-dessus */}
          <path d={COAST} className="wm-coast" />
        </g>

        {/* Noms + proximité (texte net, hors filtre) */}
        <g className="wm-labels">
          {RING.map((region) => {
            const c = byLabel.get(region.label);
            if (!c?.known_to_player) return null;
            const p = proximity(c.political_relation);
            return (
              <Label
                key={region.label}
                region={region}
                sub={`${p.label} · ${Math.round(c.political_relation)}`}
                subColor={p.ink}
              />
            );
          })}
          <Label region={HOME_REGION} sub="— vous —" home />
        </g>

        {/* Rose des vents */}
        <g className="wm-compass" transform="translate(318 180)">
          <circle r="9" className="wm-compass-ring" />
          <path d="M0 -8 L2 0 L0 8 L-2 0 Z" className="wm-compass-needle" />
          <text y="-11" textAnchor="middle" className="wm-compass-n">
            N
          </text>
        </g>

        {/* Cadre */}
        <rect x="3" y="3" width="334" height="194" className="wm-frame" />
      </svg>
    </figure>
  );
}
